import { radarConsentService } from "@/features/radar/services/radar-consent/radar-consent";
import { radarSnapshotService } from "@/features/radar/services/radar-snapshot/radar-snapshot";
import { onCourseTracerStore } from "@/features/trajectory/services/on-course-tracer-store";
import { authStorage } from "@/services/auth/auth-storage/auth-storage";
import { claimQueueManager } from "@/services/claim-queue/claim-queue-manager";
import { clearWebStorage } from "@/services/pwa/clear-web-storage/clear-web-storage";
import { deleteDatabaseSafely } from "@/services/pwa/delete-database-safely/delete-database-safely";
import {
  IDB_NAME,
  IDB_VERSION,
} from "@/services/storage/idb-constants/idb-constants";
import { storageManager } from "@/services/storage/storage/storage";
import { openDB } from "idb";

export type ResetStep = { id: string; label: string };
type ResetStepStatus = "start" | "done" | "error";
export type OnResetProgress = (
  step: ResetStep,
  status: ResetStepStatus
) => void;

/**
 * Cache Manager Service
 * Provides different levels of cache clearing for debugging and maintenance
 */
class CacheManager {
  private async runStep(
    step: ResetStep,
    fn: () => Promise<void>,
    onProgress?: OnResetProgress
  ): Promise<void> {
    onProgress?.(step, "start");
    try {
      await fn();
      onProgress?.(step, "done");
    } catch (error) {
      onProgress?.(step, "error");
      throw error;
    }
  }

  /**
   * Level 1: Clear persisted session artifacts (snapshots, tracers).
   * In-memory shot list is owned by SessionShotsContext and resets on reload.
   */
  async clearCurrentSession(onProgress?: OnResetProgress): Promise<void> {
    await this.runStep(
      { id: "session", label: "Clearing session data" },
      async () => {
        console.log("🧹 [CacheManager] Clearing current session...");
        radarSnapshotService.abortAll();
        radarConsentService.clearPendingReportDelays();
        onCourseTracerStore.clear();
        await radarSnapshotService.clearAllSnapshots();
        console.log("✅ [CacheManager] Current session cleared");
      },
      onProgress
    );
  }

  /**
   * Level 2: Clear client configuration
   * Removes setup but keeps auth and sessions
   */
  async clearClientConfig(onProgress?: OnResetProgress): Promise<void> {
    await this.endActiveSessionIfExists(onProgress);
    await this.runStep(
      { id: "config", label: "Clearing client configuration" },
      async () => {
        console.log("🧹 [CacheManager] Clearing client configuration...");
        await storageManager.clearClientConfig();
        console.log("✅ [CacheManager] Client configuration cleared");
      },
      onProgress
    );
  }

  /**
   * Level 3: Clear all app data except auth
   * Removes sessions, setup, and cached data but keeps login
   */
  async clearAllAppData(onProgress?: OnResetProgress): Promise<void> {
    await this.endActiveSessionIfExists(onProgress);

    await this.runStep(
      { id: "config", label: "Clearing client configuration" },
      async () => storageManager.clearClientConfig(),
      onProgress
    );

    await this.runStep(
      { id: "storage", label: "Clearing stored data" },
      async () => {
        await storageManager.clearAllData();
        await storageManager.clearCurrentSessionId();
      },
      onProgress
    );

    await this.runStep(
      { id: "claim-queue", label: "Clearing claim queue" },
      async () => claimQueueManager.clear(),
      onProgress
    );

    console.log("✅ [CacheManager] All app data cleared (auth preserved)");
  }

  /**
   * Level 4: Full reset (logout and clear all data)
   * Removes auth, sessions, setup, API config, and cached data
   */
  async fullReset(onProgress?: OnResetProgress): Promise<void> {
    await this.endActiveSessionIfExists(onProgress);

    await this.runStep(
      { id: "auth", label: "Signing out" },
      async () => authStorage.clearAll(),
      onProgress
    );

    await this.runStep(
      { id: "config", label: "Clearing configuration" },
      async () => {
        await storageManager.clearClientConfig();
        await storageManager.clearApiConfig();
      },
      onProgress
    );

    await this.runStep(
      { id: "storage", label: "Clearing stored data" },
      async () => storageManager.clearAllData(),
      onProgress
    );

    await this.runStep(
      { id: "claim-queue", label: "Clearing claim queue" },
      async () => claimQueueManager.clear(),
      onProgress
    );

    await this.runStep(
      { id: "web-storage", label: "Clearing local preferences" },
      async () => clearWebStorage(),
      onProgress
    );

    console.log("✅ [CacheManager] Full reset complete");
  }

  /**
   * Reinstall PWA: clear service worker caches and re-register for a fresh install.
   * All user data, auth, and configuration are preserved.
   */
  async reinstallPwa(onProgress?: OnResetProgress): Promise<void> {
    console.log("🔄 [CacheManager] Reinstalling PWA (cache only)...");

    await this.runStep(
      { id: "caches", label: "Clearing service worker caches" },
      async () => this.clearAllCaches(),
      onProgress
    );

    await this.runStep(
      { id: "service-worker", label: "Unregistering service worker" },
      async () => this.unregisterAllServiceWorkers(),
      onProgress
    );

    console.log(
      "✅ [CacheManager] PWA reinstall ready — reloading to fetch latest version"
    );
  }

  /**
   * Level 5: Nuclear reset (clear everything including service worker caches)
   * Complete fresh install - deletes the IndexedDB database, clears all
   * caches, unregisters the service worker, and wipes local/session storage.
   */
  async nuclearReset(onProgress?: OnResetProgress): Promise<void> {
    console.log("🧹 [CacheManager] Performing nuclear reset...");

    await this.endActiveSessionIfExists(onProgress);

    await this.runStep(
      { id: "auth", label: "Signing out" },
      async () => authStorage.clearAll(),
      onProgress
    );

    await this.runStep(
      { id: "storage", label: "Clearing stored data" },
      async () => {
        await storageManager.clearAllData();
        await storageManager.clearClientConfig();
        await storageManager.clearApiConfig();
      },
      onProgress
    );

    await this.runStep(
      { id: "claim-queue", label: "Clearing claim queue" },
      async () => claimQueueManager.clear(),
      onProgress
    );

    await this.runStep(
      { id: "database", label: "Deleting local database" },
      async () => {
        storageManager.closeConnection();
        await deleteDatabaseSafely(IDB_NAME);
      },
      onProgress
    );

    await this.runStep(
      { id: "caches", label: "Clearing service worker caches" },
      async () => this.clearAllCaches(),
      onProgress
    );

    await this.runStep(
      { id: "service-worker", label: "Unregistering service worker" },
      async () => this.unregisterAllServiceWorkers(),
      onProgress
    );

    await this.runStep(
      { id: "web-storage", label: "Clearing local preferences" },
      async () => clearWebStorage(),
      onProgress
    );

    console.log("✅ [CacheManager] Nuclear reset complete");
  }

  private async clearAllCaches(): Promise<void> {
    if (!("caches" in window)) {
      return;
    }
    console.log("🧹 [CacheManager] Clearing Service Worker caches...");
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
    console.log(`✅ [CacheManager] Cleared ${cacheNames.length} cache(s)`);
  }

  private async unregisterAllServiceWorkers(): Promise<void> {
    if (!("serviceWorker" in navigator)) {
      return;
    }
    console.log("🧹 [CacheManager] Unregistering Service Worker...");
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((reg) => reg.unregister()));
    console.log(
      `✅ [CacheManager] Unregistered ${registrations.length} Service Worker(s)`
    );
  }

  private async endActiveSessionIfExists(
    onProgress?: OnResetProgress
  ): Promise<void> {
    await this.clearCurrentSession(onProgress);
  }

  /**
   * Get storage statistics for debugging
   */
  async getStorageStats(): Promise<{
    indexedDB: {
      sessions: number;
      pendingRequests: number;
      offlineData: number;
    };
    indexedDBAuth: {
      hasAuth: boolean;
      hasClientConfig: boolean;
    };
    localStorage: {
      hasAuth: boolean;
      hasClientConfig: boolean;
    };
    caches: {
      count: number;
      names: string[];
    };
    serviceWorker: {
      registered: boolean;
      active: boolean;
    };
  }> {
    try {
      // IndexedDB stats
      const db = await openDB(IDB_NAME, IDB_VERSION);
      // Session data is in-memory only, no IndexedDB store
      const [pendingKeys, offlineKeys] = await Promise.all([
        db.getAllKeys("pending-requests"),
        db.getAllKeys("offline-data"),
      ]);
      db.close();

      // Auth stats (IndexedDB)
      const [hasAuth, hasClientConfig] = await Promise.all([
        authStorage.getTokens().then(Boolean),
        storageManager.getClientConfig().then(Boolean),
      ]);

      // Cache stats
      let cacheNames: string[] = [];
      if ("caches" in window) {
        cacheNames = await caches.keys();
      }

      // Service Worker stats
      let swRegistered = false;
      let swActive = false;
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        swRegistered = registrations.length > 0;
        swActive = registrations.some((reg) => !!reg.active);
      }

      return {
        indexedDB: {
          sessions: 0, // Session data is in-memory only
          pendingRequests: pendingKeys.length,
          offlineData: offlineKeys.length,
        },
        indexedDBAuth: {
          hasAuth,
          hasClientConfig,
        },
        localStorage: {
          hasAuth,
          hasClientConfig,
        },
        caches: {
          count: cacheNames.length,
          names: cacheNames,
        },
        serviceWorker: {
          registered: swRegistered,
          active: swActive,
        },
      };
    } catch (error) {
      console.error("❌ [CacheManager] Failed to get storage stats:", error);
      return {
        indexedDB: {
          sessions: 0,
          pendingRequests: 0,
          offlineData: 0,
        },
        indexedDBAuth: {
          hasAuth: false,
          hasClientConfig: false,
        },
        localStorage: {
          hasAuth: false,
          hasClientConfig: false,
        },
        caches: {
          count: 0,
          names: [],
        },
        serviceWorker: {
          registered: false,
          active: false,
        },
      };
    }
  }
}

export const cacheManager = new CacheManager();
